import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ScrollText } from "lucide-react";
import apiClient from "@/api/client";
import { Badge } from "@/components/ui/Badge";
import { PageLoader } from "@/components/ui/Spinner";

interface Agreement {
  id: string;
  contractor_name: string;
  title: string;
  signed_date: string;
  valid_until: string | null;
  line_items_count: number;
}

function isExpired(validUntil: string | null): boolean {
  if (!validUntil) return false;
  return new Date(validUntil) < new Date();
}

export function ContractorAgreementsPage() {
  const { data: agreements, isLoading } = useQuery({
    queryKey: ["contractor-agreements"],
    queryFn: () =>
      apiClient
        .get<{ data: Agreement[] }>("/agreements/")
        .then((r) => r.data.data),
  });

  if (isLoading) return <PageLoader />;

  return (
    <div className="max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <ScrollText size={22} className="text-[var(--color-primary)]" />
        <div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Umowy ramowe
          </h1>
            <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-1" />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {(agreements ?? []).length}{" "}
            {(agreements ?? []).length === 1 ? "umowa" : [2,3,4].includes((agreements ?? []).length) ? "umowy" : "umów"}
          </p>
        </div>
      </div>

      {(agreements ?? []).length === 0 ? (
        <div className="bg-white dark:bg-gray-800 p-12 text-center">
          <ScrollText size={32} className="text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">
            Brak umów ramowych.
          </p>
        </div>
      ) : (
        <>
          {/* Mobile card list */}
          <div className="sm:hidden flex flex-col divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {(agreements ?? []).map((a) => {
              const expired = isExpired(a.valid_until);
              return (
                <div key={a.id} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/contractor/agreements/${a.id}`}
                        className="font-medium text-[var(--color-primary)] hover:underline block truncate"
                      >
                        {a.title}
                      </Link>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                        {new Date(a.signed_date).toLocaleDateString("pl")}
                        {a.valid_until ? ` → ${new Date(a.valid_until).toLocaleDateString("pl")}` : ""}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{a.line_items_count} pozycji</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      {expired ? <Badge color="red">Wygasła</Badge> : <Badge color="green">Aktywna</Badge>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Desktop table */}
          <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
            <table className="w-full text-xs min-w-[600px]">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Tytuł umowy</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Data podpisania</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Ważna do</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Pozycje</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {(agreements ?? []).map((a) => {
                  const expired = isExpired(a.valid_until);
                  return (
                    <tr key={a.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3">
                        <Link to={`/contractor/agreements/${a.id}`} className="font-medium text-[var(--color-primary)] hover:underline">
                          {a.title}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {new Date(a.signed_date).toLocaleDateString("pl")}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {a.valid_until ? new Date(a.valid_until).toLocaleDateString("pl") : "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{a.line_items_count}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {expired ? <Badge color="red">Wygasła</Badge> : <Badge color="green">Aktywna</Badge>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
